import * as React from "react";
import {useSelector} from "react-redux";
import {Button, TextField} from "@material-ui/core";
import {LocalizerHelper} from "../../helpers/localizerHelper";
import {checkIfNotEmpty} from "../../helpers/validatorHelper";
import {useNavigate, useParams} from "react-router";
import {useEffect} from "react";
import PlacesAutocomplete, {geocodeByAddress} from "react-places-autocomplete";
import RestaurantHeader from "../restaurant/RestaurantHeader";
import Screen from '../screen';
import './index.scss';
import { RootState } from "../../store";
import { updateDeliveryDetails } from "../../features/session/session.service";
import { DeliveryMeasureType, Coords } from "../../models/restaurant";
import InfoDetailsDelivery from "./InfoDetailsDelivery";

const LocationServiceAvailable = () => {
    const { restaurant: restaurantId, dits: sessionId, menu: menuId, link_alias: linkAlias } = useParams<{ restaurant: string; dits: string, menu:string, link_alias: string }>();
    const restaurant = useSelector((state: RootState) => state.restaurant.restaurant);
    const session = useSelector((state: RootState) => state.session.session);

    const navigate = useNavigate();

    const [address, setAddress] = React.useState('');

    const [deliveryCost, setDeliveryCost] = React.useState<number>(0);
    const [distance, setDistance] = React.useState<number>(0);
    const [type, setType] = React.useState<DeliveryMeasureType>(DeliveryMeasureType.DEFAULT);

    const [coords1, setCoords1] = React.useState<Coords>({lat:0, lng:0});
    const [coords2, setCoords2] = React.useState<Coords>({lat:0, lng:0});

    const [canDeliver, setCanDeliver] = React.useState(false);

    const [firstAttempt, setFirstAttempt] = React.useState(true);

    const [error, setError] = React.useState({
        address: !checkIfNotEmpty(address),
    });

    const lastLinkAlias = (window.localStorage) ? (window.localStorage.getItem('chuzeat_last_alias_link') ?? '' ) : '';

    useEffect(() => {
        if(restaurant){
            setDeliveryCost(restaurant.delivery_cost ?? 0);
        }
    }, [restaurant]);

    useEffect(() => {
        const savedAddress = session?.delivery_details?.address;
        if (savedAddress && savedAddress !== address) {
            onSelectAddress(savedAddress);
        }
    }, [session?.delivery_details?.address]);

    const setAddressValue = (value: string) => {
        const _error = {...error};
        setAddress(value);
        _error.address = !checkIfNotEmpty(value ?? address);
        setError(_error);
    }

    const onChangeAddress = async (address: string) => {
        setCanDeliver(false);
        setAddressValue(address);
    }

    const calculateDelivery = async (
        from: Coords,
        to: Coords
    ): Promise<number> => {
        return new Promise((resolve, reject) => {
            const service = new google.maps.DirectionsService();

            service.route(
            {
                origin: from,
                destination: to,
                travelMode: google.maps.TravelMode.DRIVING,
                unitSystem: google.maps.UnitSystem.METRIC,
            },
            (result, status) => {
                if (status !== "OK" || !result) {
                    reject(status);
                    return;
                }

                const meters = result.routes[0].legs[0].distance?.value ?? 0;
                resolve(meters / 1000);
            }
            );
        });
    };

    const computeDeliveryCost = (distance: number, restaurant: any) => {
        const base = restaurant.delivery_cost ?? 0;
        const freeRange = restaurant.min_range_free ?? 0;
        const measure = restaurant.delivery_measure_type ?? DeliveryMeasureType.DEFAULT;
        const measureCost = restaurant.delivery_measure_cost ?? 0;

        if (distance <= freeRange) return base;

        if (measure === DeliveryMeasureType.KM) {
            return Math.ceil(distance - freeRange) * measureCost + base;
        }

        return base;
    };


    const onSelectAddress = async (address: string) => {
        if (!restaurant?.coords) return;

        setFirstAttempt(false);
        setCanDeliver(false);
        setAddressValue(address);

        const results = await geocodeByAddress(address);
        if (!results.length) return;

        const customerCoords = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
        };

        const restaurantCoords = restaurant.coords;

        setCoords1(customerCoords);
        setCoords2(restaurantCoords);

        try {
            const km = await calculateDelivery(customerCoords, restaurantCoords);
            const canDeliver = km <= (restaurant.max_delivery_distance ?? 0);
            const cost = computeDeliveryCost(km, restaurant);

            console.log(`km [${km}] canDeliver [${canDeliver}] cost [${cost}]`);

            setDistance(km);
            setCanDeliver(canDeliver);
            setDeliveryCost(cost);
            setType(restaurant.delivery_measure_type ?? DeliveryMeasureType.DEFAULT);
        } catch (e) {
            console.error("Directions error", e);
        }
    };


    const onAutoCompleteError = (error: string) => {
        console.log('@@@@@@error');
        console.log(error);
    }

    const _proceedWithOrder = async () => {
        if (!restaurantId || !sessionId || !restaurant) return;

        try {
            await updateDeliveryDetails(restaurantId, sessionId, {
                address,
                distance,
                delivery_measure_type: type,
                preview_delivery_cost: Number(deliveryCost.toFixed(2)),
                location1: coords1,
                location2: coords2,
            });

            const alias = linkAlias ?? lastLinkAlias;
            navigate(`/menu/${alias}/menu/${restaurantId}/${sessionId}/${menuId}`);
        } catch (err) {
            console.error("Failed to update delivery details", err);
        }
        };

    const renderFunction =  ({ getInputProps, getSuggestionItemProps, suggestions }: any) => (
        <div className="screen_location_service">
            <RestaurantHeader className='block'/>
            <div className="container">
                <div className="user_questions">{LocalizerHelper.localized('location_service_available_header')}</div>
                <InfoDetailsDelivery deliveryCost={deliveryCost}/>
                <div className="row container-input-service_available">
                    <div className="container-suggestion-label-service_available">
                        {firstAttempt && 
                            <div className="col-md-12">
                                {LocalizerHelper.localized('select_address_from_suggestions')}
                            </div>
                        }
                        {!firstAttempt && !canDeliver && 
                            <div className="col-md-12" style={{color: "#FA453B", fontWeight: "bold"}}>
                                {LocalizerHelper.localized('restaurant_not_deliver_at_this_address')}
                            </div>
                        }
                    </div>
                    <div className="col-md-12">
                        <TextField
                            {...getInputProps()}
                            variant="outlined"
                            name="address"
                            error={error.address}
                            fullWidth={true}
                            helperText={LocalizerHelper.localized('field_required')}
                            label={LocalizerHelper.localized('address')}>

                        </TextField>
                    </div>
                    <div className="col-md-12">
                        <div className={`container-autocomplete-service_available ${(suggestions && suggestions.length > 0) ? 'open' : ''}`}>
                            <div className="autocomplete-dropdown">
                                {suggestions.map((suggestion: any, index: number) => (
                                    <div {...getSuggestionItemProps(suggestion)} key={`suggestion-${index}`} className="address-suggestion">
                                        {suggestion.description}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            <div className="row container-buttons-action">
                <div className="col-md-12">
                    <Button className='accent-button full-width button button' disabled={!canDeliver}
                            onClick={() => _proceedWithOrder()}>
                        {LocalizerHelper.localized('confirm')}
                    </Button>
                </div>
            </div>
        </div>
        </div>
    );

    return (<Screen className="screen">
        {window.google?.maps.places && <PlacesAutocomplete
            value={address}
            onSelect={onSelectAddress}
            onChange={onChangeAddress}
            onError={onAutoCompleteError}
            searchOptions={
                restaurant
                ? {
                    location: new google.maps.LatLng(
                        restaurant.coords.lat,
                        restaurant.coords.lng
                    ),
                    radius: (restaurant.max_delivery_distance ?? 10) * 1000,
                    //componentRestrictions: { country: "it" },
                    }
                : undefined
            }>
            {renderFunction}
        </PlacesAutocomplete>}
    </Screen>);
};

export default LocationServiceAvailable;
